import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dimmer,
  Dropdown,
  Grid,
  GridColumn,
  Loader,
} from 'semantic-ui-react';

import {
  getPropertySettings,
  setEditPropertySetting,
  resetPropertySettings,
} from '../../redux/actions/clients';
import { SystemSetting } from '../../types';
import EntityData from '../Common/EntityData';
import { useEffect, useState } from 'react';
import Filters from '../Common/Filters';
import useClientProperties from '../../hooks/useClientProperties';
import { StoreState } from '../../types/state';

type PropertySettingsProps = {
  clientId: string;
  client: any;
};

const PropertySettings = (props: PropertySettingsProps) => {
  const { clientId, client } = props;
  const { properties, isLoading: propertiesLoading } =
    useClientProperties(clientId);

  const [clientPropertySettings, settingsLoading] = useSelector(
    (state: StoreState) => [
      state.clients.propertySettings[clientId] || {},
      state.clients.isLoading,
    ]
  );

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState();

  const dispatch = useDispatch();
  const handleEdit = (setting: SystemSetting) => {
    dispatch(setEditPropertySetting(setting, clientId, selectedProperty));
  };

  const [filteredSettings, setFilteredSettings] = useState([]);

  useEffect(() => {
    if (!propertiesLoading && properties) {
      const filtered = properties.filter((property) =>
        client.ManagedProperties
          ? client.ManagedProperties.includes(String(property.value))
          : true
      );
      setPropertyOptions(filtered);
      setSelectedProperty(filtered[0].value);
    }
  }, [properties, propertiesLoading, client]);

  useEffect(() => {
    if (clientPropertySettings && selectedProperty) {
      if (!clientPropertySettings[selectedProperty] && !settingsLoading) {
        dispatch(getPropertySettings(clientId, selectedProperty));
      }
    }
  }, [
    selectedProperty,
    clientPropertySettings,
    clientId,
    dispatch,
    settingsLoading,
  ]);

  const handlePropertyChange = (e, { value }) => {
    setSelectedProperty(value);
  };

  const handleReset = () => {
    dispatch(resetPropertySettings(clientId, selectedProperty));
  };

  return (
    <div>
      {propertiesLoading || settingsLoading ? (
        <Dimmer inverted active>
          <Loader />
        </Dimmer>
      ) : null}
      <>
        {filteredSettings && (
          <>
            <Grid padded="vertically">
              <GridColumn width={6}>
                <div>
                  Filters:
                  <Filters
                    filteredLists={filteredSettings}
                    originalList={
                      (clientPropertySettings &&
                        selectedProperty &&
                        clientPropertySettings[selectedProperty]) ||
                      []
                    }
                    allowedParams={['Key', 'Name', 'Description', 'Value']}
                    setFilteredLists={setFilteredSettings}
                  />
                </div>
              </GridColumn>
              <GridColumn width={6} verticalAlign="middle">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Property:
                  <Dropdown
                    placeholder="Select Property"
                    value={selectedProperty}
                    fluid
                    search
                    selection
                    options={propertyOptions.map((item) => ({
                      ...item,
                      text: `${item.text} - ${item.value}`,
                    }))}
                    onChange={handlePropertyChange}
                  />
                </div>
              </GridColumn>
              <GridColumn width={4}>
                <Button basic onClick={handleReset}>
                  Reset to default
                </Button>
              </GridColumn>
            </Grid>

            {filteredSettings?.map((setting) => {
              return (
                <EntityData
                  key={setting.Key}
                  entity={setting}
                  enableEdit={true}
                  onEdit={() => handleEdit(setting)}
                />
              );
            })}
          </>
        )}
      </>
    </div>
  );
};

export default PropertySettings;
